<template>
  <div>
    <div :class="$style.headerSection">
      <div :class="$style.actionsSection">
        <div>
          <div :class="$style.selectAll">
            <Checkbox v-model="selectAll" binary style="margin-right: 0.5rem" @update:model-value="selectAllOffers" />
            Select all ({{ visibleOffers.length }})
          </div>
          <div :class="$style.expandAllSection">
            <ToggleSwitch v-model="expandAll" />
            <label for="expandAll">Expand all</label>
          </div>
        </div>
        <div>
          <InputText
            v-model="search"
            placeholder="Search offers"
            :class="$style.searchInput"
            @input="handleSearchOffers"
          />
          <Button
            label="New"
            icon="pi pi-chevron-down"
            icon-pos="right"
            :disabled="!isWipVersion"
            @click="toggleNewOptions"
          />
          <Popover ref="newOptionsRef">
            <div :class="$style.popoverOptions">
              <div @click="showCampaignModal">New campaign</div>
              <div @click="showCreateModal">New offer</div>
            </div>
          </Popover>
          <Button
            label="CSV"
            icon="pi pi-chevron-down"
            icon-pos="right"
            :disabled="!isWipVersion"
            @click="toggleCsvOptions"
          />
          <Popover ref="csvOptionsRef">
            <div :class="$style.popoverOptions">
              <div @click="csvDropdownChange({ value: 'upload' })">Upload Offers</div>
              <div :class="$style.campaignUploadOption" @click="csvDropdownChange({ value: 'upload-campaigns' })">
                <span>Upload Campaigns</span>
                <VdIcon
                  v-tooltip.top="csvCampaignUploadTooltip"
                  store="fas"
                  name="circle-info"
                  color="var(--vd-color-blue-default)"
                  @click.stop
                />
              </div>
              <div @click="csvDropdownChange({ value: 'download' })">Download Template</div>
            </div>
          </Popover>
        </div>
      </div>
    </div>
    <div v-if="selectedOffers.size && isWipVersion" :class="$style.selectOffersActions">
      <div>
        <div>{{ selectedOffers.size }} {{ pluralize("offer", selectedOffers.size) }} selected</div>
        <div v-if="areDraftSelected" :class="$style.draftWarning">
          <VdIcon store="fa" name="triangle-exclamation" color="var(--vd-color-error)"></VdIcon>
          When updating a draft offer, make sure that the rewards section is completed.
        </div>
      </div>
      <div>
        <Select
          v-model="selectedAction"
          placeholder="Actions"
          option-label="label"
          option-value="value"
          :options="actionItems"
          style="margin-right: 0.75rem"
          :option-disabled="handleActionDisabled"
          @update:model-value="handleBulkStateUpdate"
        >
          <template #option="slotProps">
            <div class="flex align-items-center">
              <span>{{ slotProps.option.label }}</span>
              <span v-if="computeOffersThanCanBeUpdated(slotProps.option.value)" style="margin-left: 0.25rem">
                ({{ computeOffersThanCanBeUpdated(slotProps.option.value) }})
              </span>
            </div>
          </template>
        </Select>
        <span
          v-if="flags.offerMultiLinkABTest.enabled"
          v-tooltip.top="bulkLinkDisabledReason"
          :class="$style.bulkLinkButtonWrapper"
        >
          <Button
            :label="`Link to ABTest (${selectedOffers.size})`"
            icon="pi pi-bullseye"
            :disabled="!!bulkLinkDisabledReason"
            @click="showBulkAbtestModal"
          />
        </span>
        <Button label="Delete" icon="pi pi-trash" @click="handleBulkDeleteOffers" />
      </div>
    </div>
    <FileUpload
      ref="csvFileUpload"
      :key="csvFileUploadKey"
      :class="$style.uploadButton"
      :max-file-size="1048576"
      :file-limit="1"
      mode="basic"
      name="file"
      accept="text/csv"
      choose-label="Upload Offers"
      custom-upload
      :auto="true"
      style="display: none"
      @uploader="csvHandleFileUpload"
    />
    <FileUpload
      ref="csvCampaignsFileUpload"
      :key="csvCampaignsFileUploadKey"
      :class="$style.uploadButton"
      :max-file-size="1048576"
      :file-limit="1"
      mode="basic"
      name="file"
      accept="text/csv"
      choose-label="Upload Campaigns"
      custom-upload
      :auto="true"
      style="display: none"
      @uploader="csvHandleCampaignsFileUpload"
    />

    <div :class="$style.sortSection">
      <OfferSortAndFilter
        v-model:sortOrder="sortOrder"
        v-model:selectedStatuses="selectedStatuses"
        v-model:selectedCampaigns="selectedCampaigns"
        :game-id="gameId"
      />
    </div>
    <div v-if="queryLoading && areOffersEmpty" :class="$style.loadingContainer">
      <ProgressSpinner style="stroke: var(--vd-color-blue-default)" />
    </div>
    <VTEmptyState
      v-else-if="areOffersEmpty"
      :title="emptyState.title"
      :description="emptyState.description"
      :button-text="emptyState.buttonText"
      @empty-state-action="showCreateModal"
    />
    <div v-if="!areOffersEmpty">
      <CampaignCollapsor
        v-for="(campaign, index) in searchedCampaigns"
        :key="campaign.id"
        :campaign="campaign"
        :is-content-visible="expandAllCampaigns"
        :selected-campaigns="selectedCampaigns"
        :is-first-campaign="index === 0"
        :offers="campaign.offers"
        :is-wip-version="isWipVersion"
        :selectable="flags.offerMultiLinkABTest.enabled && isWipVersion"
        :selection-state="campaignSelectionState(campaign)"
        :total-offers-count="totalOffersCount(campaign.id)"
        @toggle-select-campaign="handleCampaignSelection(campaign, $event)"
      >
        <OfferItem
          v-for="offer in campaign.offers"
          :id="offer.id"
          :key="offer.technicalName"
          :name="offer.name"
          :title="offer.title"
          :technical-name="offer.technicalName"
          :status="offer.status"
          :start-date="offer.startDate"
          :end-date="offer.endDate"
          :conditions="offer.conditions"
          :triggers="offer.triggers"
          :tags="offer.tags"
          :android-iap="offer.androidIAP"
          :ios-iap="offer.iosIAP"
          :is-content-visible="expandAllOffers"
          :is-selected="selectedOffers.has(offer.id)"
          :timer="offer.timer"
          :display-count-down="offer.displayCountDown"
          :menu-badge="offer.menuBadge"
          :image="offer.image"
          :highlight-texts="offer.highlightTexts"
          :is-wip-version="isWipVersion"
          :ab-test-id="offer.abTestId"
          :abtest-link-mode="offer.abtestLinkMode"
          :abtest-links="offer.abtestLinks"
          :campaign-id="offer.campaignId"
          :type="offer.type"
          :is-endless="offer.isEndless"
          :is-chained="offer.isChained"
          :offer-buy-all-details="offer.offerBuyAllDetails"
          :class="$style.offerItem"
          @update-conditions="handleOfferConditionUpdate(offer.id, $event)"
          @duplicate="handleOfferDuplicate(offer.id)"
          @update-timer="handleOfferTimerUpdate(offer.id, $event)"
          @update-in-app-purchase="handleIAPUpdate(offer.id, $event)"
          @update-dates="handleUpdateDates(offer.id, $event)"
          @update-menu-badge="handleUpdateMenuBadge(offer.id, $event)"
          @select-offer="handleOfferSelection(offer.id, offer.status, $event)"
          @delete-offer="handleOfferDeletion(offer.id)"
          @mark-as-ready="handleOfferStatusUpdate(offer.id, OfferStatus.Pending)"
          @mark-as-draft="handleOfferStatusUpdate(offer.id, OfferStatus.Draft)"
          @cancel-stopping-offer="handleOfferStatusUpdate(offer.id, OfferStatus.Published)"
          @update-name="handleOfferNameUpdate(offer.id, $event)"
          @update-title="handleOfferTitleUpdate(offer.id, $event)"
          @update-highlight-texts="handleHighlightTextsUpdate(offer.id, $event)"
          @stop-offer="handleOfferStatusUpdate(offer.id, OfferStatus.Terminating)"
          @update-image="handleOfferImageUpdate(offer.id, $event)"
          @update-selected-triggers="handleTriggerUpdate(offer.id, $event)"
          @update-tags="handleTagsUpdate(offer.id, $event)"
          @update-display-countdown="handleDisplayCountdownUpdate(offer.id, $event)"
          @update-display-countdown-in-menu-badge="handleDisplayCountdownInMenuBadgeUpdate(offer.id, $event)"
          @update-abtest-link-mode="handleAbtestLinkModeUpdate(offer.id, $event)"
          @update-offer-campaign="handleOfferCampaignUpdate(offer.id, $event)"
          @update-offer-type="handleOfferTypeUpdate(offer.id, $event)"
          @update-offer-is-chained="handleOfferIsChainedUpdate(offer.id, $event)"
          @update-offer-is-endless="handleOfferIsEndlessUpdate(offer.id, $event)"
          @update-buy-all-details="handleBuyAllDetailsUpdate(offer.id, $event)"
        />
      </CampaignCollapsor>
    </div>
  </div>
  <OfferForm
    :is-visible="offerModalVisible"
    :loading="creatingOffer"
    @save-offer="handleSaveOffer"
    @close="handleModalClose"
  />
  <CampaignForm :is-visible="isCampaignModalVisible" @close="handleCampaignModalClose" />
  <BulkAbtestTargetingModal
    v-if="bulkAbtestModalVisible"
    :is-visible="bulkAbtestModalVisible"
    :game-id="gameId"
    :version-id="versionId"
    :offers="selectedOfferObjects"
    :campaigns="sortedCampaigns"
    @close="bulkAbtestModalVisible = false"
    @applied="handleBulkAbtestApplied"
  />
  <CSVImportModal
    :is-visible="csvModalVisible"
    :loading="csvLoading"
    :errors="csvErrors"
    :count="csvCount"
    :success="csvSuccess"
    :entity-label="csvEntityLabel"
    @close="csvHandleModalClose"
  />
</template>

<script setup>
import { useBulkDeleteOffersMutation, useBulkUpdateOffersMutation, useCreateOfferMutation } from "@/graphql";
import { useDebounceFn } from "@vueuse/core";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import FileUpload from "primevue/fileupload";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import ProgressSpinner from "primevue/progressspinner";
import Select from "primevue/select";
import ToggleSwitch from "primevue/toggleswitch";
import { useConfirm } from "primevue/useconfirm";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import VTEmptyState from "@/components/VTEmptyState";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { pluralize } from "@/filters";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useToast from "@/hooks/useToastNotification";
import { useOffers } from "@/modules/Offers/hooks/useOffers";
import { OfferStatus } from "@/modules/Offers/types/types";
import formatDateTime from "@/utils/formatDateTime";
import OfferItem from "./OfferItem.vue";
import BulkAbtestTargetingModal from "./components/BulkAbtestTargetingModal.vue";
import CSVImportModal from "./components/CSVImportModal.vue";
import CampaignCollapsor from "./components/CampaignCollapsor.vue";
import CampaignForm from "./components/CampaignForm.vue";
import OfferForm from "./components/OfferForm.vue";
import OfferSortAndFilter from "./components/OfferSortAndFilter.vue";

import uploadCSV from "./uploadCSV";
import uploadCampaignsCSV from "./uploadCampaignsCSV";

const { toastSuccess, toastError, toastWarning } = useToast();

const route = useRoute();
const gameId = ref(route.params.gameId);
const versionId = ref(route.params.versionId);

const isWipVersion = computed(() => versionId.value === "wip");
const { flags } = useFeatureFlags(gameId.value);
const csvFileUpload = ref(null);
const csvFileUploadKey = ref(0);
const csvCampaignsFileUpload = ref(null);
const csvCampaignsFileUploadKey = ref(0);
const csvModalVisible = ref(false);
const csvLoading = ref(false);
const csvErrors = ref([]);
const csvCount = ref(0);
const csvSuccess = ref(false);
const csvEntityLabel = ref("Offers");
const search = ref("");
const selectAll = ref(false);
const offerModalVisible = ref(false);
const isCampaignModalVisible = ref(false);
const selectedAction = ref(null);
const actionItems = computed(() => [
  { label: "Set as draft", value: OfferStatus.Draft },
  { label: "Set as pending", value: OfferStatus.Pending },
  { label: "Set as terminating", value: OfferStatus.Terminating },
  // Multi-link: offer status is decoupled from A/B-test linking, so a terminating offer can be republished
  ...(flags.value.offerMultiLinkABTest.enabled ? [{ label: "Undo terminating", value: OfferStatus.Published }] : []),
]);

const confirm = useConfirm();
const { mutate: bulkDeleteOffersMutation } = useBulkDeleteOffersMutation({});
const { mutate: bulkUpdateOffersMutation } = useBulkUpdateOffersMutation({});

const {
  refetchOffers,
  updateOffer,
  duplicateOffer,
  deleteOffer,
  sortOrder,
  selectedStatuses,
  selectedCampaigns,
  getCampaigns,
  sortedCampaigns,
  queryLoading,
} = useOffers(gameId.value, versionId.value);

const sortedOffers = computed(() => sortedCampaigns.value.flatMap((campaign) => campaign.offers));

const showCreateModal = () => {
  offerModalVisible.value = true;
};

const handleModalClose = () => {
  offerModalVisible.value = false;
};

const showCampaignModal = () => {
  isCampaignModalVisible.value = true;
};

const handleCampaignModalClose = () => {
  isCampaignModalVisible.value = false;
};

const { mutate: createOffer, loading: creatingOffer } = useCreateOfferMutation({});

const areOffersEmpty = computed(() => sortedOffers.value.length === 0);
const selectedOffers = ref(new Map());

const newOptionsRef = ref();

const toggleNewOptions = (event) => {
  newOptionsRef.value.toggle(event);
};

const csvOptionsRef = ref();

const toggleCsvOptions = (event) => {
  csvOptionsRef.value.toggle(event);
};

const areDraftSelected = computed(() => {
  const statuses = Array.from(selectedOffers.value.values());
  return statuses.includes(OfferStatus.Draft);
});

const emptyState = {
  title: "No offers",
  description: "You have no offers for the time being, to create a new offer, use the button below",
  buttonText: "New offer",
};

const expandAll = ref(false);
const expandAllCampaigns = ref(false);
const expandAllOffers = ref(false);

watch(expandAll, async (newValue) => {
  expandAllCampaigns.value = newValue;

  setTimeout(() => {
    expandAllOffers.value = newValue;
  }, 100);
});

const searchedCampaigns = ref([]);

// The offers currently shown (search-filtered) — selection always operates on these.
const visibleOffers = computed(() => searchedCampaigns.value.flatMap((campaign) => campaign.offers));

const handleSearchOffers = useDebounceFn((event) => {
  selectedOffers.value.clear();
  const searchValue = event.target.value;

  searchedCampaigns.value = sortedCampaigns.value.map((campaign) => ({
    ...campaign,
    offers: campaign.offers?.filter((offer) => offer.name.toLowerCase().includes(searchValue.toLowerCase())) || [],
  }));
}, 500);

const bulkDeleteOffers = async (offerIds) => {
  const res = await bulkDeleteOffersMutation({
    gameId: gameId.value,
    versionId: versionId.value,
    offerIds,
  });

  if (res?.data?.bulkDeleteOffers?.success) {
    refetchOffers();
    toastSuccess({
      detail: `${offerIds.length} ${pluralize("offer", offerIds.length)} deleted successfully!`,
      summary: "Success",
      life: 3 * 1000,
    });
  } else {
    toastError({
      detail:
        res?.data?.bulkDeleteOffers?.details ||
        `An error occurred while deleting the ${pluralize("offer", offerIds.length)}`,
      life: 3 * 1000,
      summary: "Error",
    });
  }
};

const handleBulkDeleteOffers = async () => {
  const selectedOffersIds = Array.from(selectedOffers.value.keys());

  confirm.require({
    message: `Are you sure want to delete ${selectedOffersIds.length} ${pluralize(
      "offer",
      selectedOffersIds.length
    )} ?`,
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    acceptIcon: "pi pi-trash",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: async () => {
      try {
        await bulkDeleteOffers(selectedOffersIds);
        selectedOffers.value.clear();
        await refetchOffers();
      } catch (error) {
        console.log(error, "error");
      }
    },
  });
};

const handleOfferConditionUpdate = async (offerId, conditions) => {
  try {
    const updatePayload = {
      conditions,
    };
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer conditions:", error);
  }
};

const handleAbtestLinkModeUpdate = async (offerId, abtestLinkMode) => {
  try {
    // useMultiLink gates the backend's multi-link behaviors: flag-on, a mode FLIP clears all the offer's
    // links (the ABTest tab confirms with the user before emitting).
    await updateOffer(offerId, {
      abtestLinkMode,
      useMultiLink: flags.value.offerMultiLinkABTest.enabled,
    });
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer A/B test mode:", error);
  }
};

const handleOfferTimerUpdate = async (offerId, timer) => {
  try {
    const { displayCountDown, menuBadge, ...timerProps } = timer;
    const updatePayload = {
      timer: timerProps,
      displayCountDown,
    };
    if (menuBadge) {
      updatePayload.menuBadge = menuBadge;
    }
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer timer:", error);
  }
};

const handleDisplayCountdownInMenuBadgeUpdate = async (offerId, displayCountDownInMenuBadge) => {
  const updatePayload = {
    menuBadge: { displayCountDownInMenuBadge },
  };
  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer display countdown in menu badge:", error);
  }
};

const handleSaveOffer = async (offer) => {
  const res = await createOffer({
    appId: gameId.value,
    versionId: versionId.value,
    input: offer,
  });

  if (res?.data?.createOffer?.success) {
    handleModalClose();
    refetchOffers();
    toastSuccess({
      detail: `The offer ${offer.name} has been created successfully!`,
      summary: "Success",
      life: 3 * 1000,
    });
  } else {
    toastError({
      detail: res?.data?.createOffer?.details,
      life: 3 * 1000,
      summary: "Error",
    });
  }
};

const handleOfferDuplicate = async (offerId) => {
  try {
    await duplicateOffer(offerId);
    await refetchOffers();
  } catch (error) {
    console.error("Error duplicating offer:", error);
  }
};

const handleIAPUpdate = async (offerId, payload) => {
  const updatePayload = {
    iosIAP: payload.iosIAP,
    androidIAP: payload.androidIAP,
  };
  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer IAP:", error);
  }
};

const handleUpdateDates = async (offerId, dates) => {
  const formattedDates = {
    startDate: formatDateTime(dates.startDate),
    endDate: formatDateTime(dates.endDate),
  };

  try {
    await updateOffer(offerId, formattedDates);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer dates:", error);
  }
};

const handleUpdateMenuBadge = async (offerId, menuBadge) => {
  const offerUpdatePayload = {
    menuBadge,
  };
  try {
    await updateOffer(offerId, offerUpdatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer menu badge:", error);
  }
};

function handleOfferSelection(offerId, status, selected) {
  if (selected) {
    selectedOffers.value.set(offerId, status);
  } else {
    selectedOffers.value.delete(offerId);
  }
}

function selectAllOffers(value) {
  if (value) {
    visibleOffers.value.forEach((offer) => {
      handleOfferSelection(offer.id, offer.status, true);
    });
  } else {
    selectedOffers.value.clear();
  }
}

// Campaign-level selection: toggles only the campaign's currently-visible (search-filtered) offers.
function handleCampaignSelection(campaign, selected) {
  campaign.offers.forEach((offer) => {
    handleOfferSelection(offer.id, offer.status, selected);
  });
}

function campaignSelectionState(campaign) {
  const selectedCount = campaign.offers.filter((offer) => selectedOffers.value.has(offer.id)).length;
  if (selectedCount === 0) {
    return "none";
  }
  return selectedCount === campaign.offers.length ? "all" : "some";
}

function totalOffersCount(campaignId) {
  return sortedCampaigns.value.find((campaign) => campaign.id === campaignId)?.offers?.length ?? 0;
}

const selectedOfferObjects = computed(() => sortedOffers.value.filter((offer) => selectedOffers.value.has(offer.id)));

// The bulk A/B-test targeting action needs a uniform selection: every offer PENDING/PUBLISHED and all
// sharing the same segment set (one treatment resolution valid for all). Returns the tooltip reason, or
// undefined when the action is available.
const bulkLinkDisabledReason = computed(() => {
  const offers = selectedOfferObjects.value;
  if (!offers.length) {
    return "Select at least one offer";
  }
  if (offers.some((offer) => offer.status !== OfferStatus.Pending && offer.status !== OfferStatus.Published)) {
    return "Only pending or published offers can be linked to A/B tests";
  }
  // Offers may use different segments: the modal shows the segments in use, filters tests to those covering
  // all of them, and the backend links each offer via its own segment's treatment arm.
  if (offers.some((offer) => !(offer.conditions?.segments ?? []).length)) {
    return "Offers without a segment cannot be linked to A/B tests";
  }
  return undefined;
});

const bulkAbtestModalVisible = ref(false);

const showBulkAbtestModal = () => {
  bulkAbtestModalVisible.value = true;
};

const handleBulkAbtestApplied = async (result) => {
  bulkAbtestModalVisible.value = false;
  const updatedCount = result?.successfulOfferIds?.length ?? 0;
  selectedOffers.value.clear();
  await refetchOffers();
  toastSuccess({
    detail: `A/B-test targeting updated on ${updatedCount} ${pluralize("offer", updatedCount)}`,
    summary: "Success",
    life: 3 * 1000,
  });
};

const handleOfferStatusUpdate = async (offerId, offerStatus) => {
  const updatePayload = {
    status: offerStatus,
    // Multi-link: lets linked offers change status (backend still refuses DRAFT while linked)
    useMultiLink: flags.value.offerMultiLinkABTest.enabled,
  };
  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error(`Error marking offer ${offerStatus}`, error);
  }
};

async function handleOfferDeletion(offerId) {
  try {
    await deleteOffer(offerId);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer menu badge:", error);
  }
}

const handleOfferImageUpdate = async (offerId, image) => {
  const updatePayload = {
    image,
  };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer image:", error);
  }
};

const handleOfferNameUpdate = async (offerId, newName) => {
  const updatePayload = { name: newName };

  const res = await updateOffer(offerId, updatePayload);

  if (res?.data?.updateOfferMutation.success) {
    await refetchOffers();
  } else {
    toastError({
      detail: res?.data?.updateOfferMutation.details,
      life: 3 * 1000,
      summary: "Error",
    });
  }
};

const handleOfferTitleUpdate = async (offerId, newTitle) => {
  const updatePayload = { title: newTitle };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer title:", error);
  }
};

const handleHighlightTextsUpdate = async (offerId, newValues) => {
  const updatePayload = { highlightTexts: newValues };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating highlight texts:", error);
  }
};

const handleTriggerUpdate = async (offerId, updatedTriggers) => {
  const triggerIds = updatedTriggers.map((trigger) => trigger.id);

  const updatePayload = {
    triggers: triggerIds,
  };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer triggers:", error);
  }
};

const handleTagsUpdate = async (offerId, updatedTags) => {
  const updatePayload = { tags: updatedTags };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating tags:", error);
  }
};

const handleOfferCampaignUpdate = async (offerId, updatedCampaign) => {
  const updatePayload = {
    campaignId: updatedCampaign?.value ?? null,
  };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer campaign:", error);
  }
};

const handleOfferTypeUpdate = async (offerId, offerType) => {
  const updatePayload = {
    type: offerType,
  };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer type:", error);
  }
};

// Flag-on (multi-link) apps get the template with the abtest_mode + abtest_N/cohort_N columns.
const csvTemplateUrl = computed(() =>
  flags.value.offerMultiLinkABTest.enabled
    ? "https://docs.google.com/spreadsheets/d/1fNaDP5GtcmBfKPZtIQbQ2a3gNn8juHe4"
    : "https://docs.google.com/spreadsheets/d/1grzY7QnUZwCHkxZkzajoOBRQXqD6Yjfa"
);

const csvCampaignUploadTooltip =
  "This import is all-or-nothing. If any row fails validation, the whole file is rejected and no campaigns are created or changed.";

const csvDropdownChange = async (selectedValue) => {
  if (selectedValue.value === "download") {
    window.open(csvTemplateUrl.value, "_blank");
  } else if (selectedValue.value === "upload") {
    csvFileUpload.value.$el.querySelector('input[type="file"]').click();
  } else if (selectedValue.value === "upload-campaigns") {
    csvCampaignsFileUpload.value.$el.querySelector('input[type="file"]').click();
  }
};

const csvHandleModalClose = () => {
  csvModalVisible.value = false;
  csvFileUploadKey.value += 1;
  csvCampaignsFileUploadKey.value += 1;
};

const handleCsvImportResult = (importCsv) => {
  csvSuccess.value = importCsv.success;
  csvCount.value = importCsv.count;
  csvErrors.value = importCsv.errors;
};

const csvHandleFileUpload = async (event) => {
  const file = event.files[0];

  csvEntityLabel.value = "Offers";
  csvLoading.value = true;
  csvModalVisible.value = true;

  const importCsv = await uploadCSV(gameId.value, versionId.value, file);

  csvLoading.value = false;

  handleCsvImportResult(importCsv);

  if (importCsv.success) {
    await refetchOffers();
  }
};

const csvHandleCampaignsFileUpload = async (event) => {
  const file = event.files[0];

  csvEntityLabel.value = "Campaigns";
  csvLoading.value = true;
  csvModalVisible.value = true;

  const importCsv = await uploadCampaignsCSV(gameId.value, versionId.value, file);

  csvLoading.value = false;

  handleCsvImportResult(importCsv);

  if (importCsv.success) {
    await Promise.all([getCampaigns(), refetchOffers()]);
  }
};

const handleOfferIsChainedUpdate = async (offerId, isChained) => {
  const updatePayload = {
    isChained,
  };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer is chained:", error);
  }
};

const handleOfferIsEndlessUpdate = async (offerId, isEndless) => {
  const updatePayload = {
    isEndless,
  };

  try {
    await updateOffer(offerId, updatePayload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer is endless:", error);
  }
};

const handleBuyAllDetailsUpdate = async (offerId, payload) => {
  try {
    await updateOffer(offerId, payload);
    await refetchOffers();
  } catch (error) {
    console.error("Error updating offer buy all details:", error);
  }
};

function handleActionDisabled(dropdownAction) {
  const selectedOffersStatuses = Array.from(selectedOffers.value.values());

  switch (dropdownAction.value) {
    case OfferStatus.Draft:
    case OfferStatus.Pending:
      return (
        !selectedOffersStatuses.includes(OfferStatus.Pending) && !selectedOffersStatuses.includes(OfferStatus.Draft)
      );
    case OfferStatus.Terminating:
      return !selectedOffersStatuses.includes(OfferStatus.Published);
    case OfferStatus.Published:
      return !selectedOffersStatuses.includes(OfferStatus.Terminating);
    default:
      return true;
  }
}

function computeOffersThanCanBeUpdated(action) {
  const selectedOffersStatuses = Array.from(selectedOffers.value.values());

  switch (action) {
    case OfferStatus.Draft:
    case OfferStatus.Pending:
      return (
        selectedOffersStatuses.filter((status) => status === OfferStatus.Draft).length +
        selectedOffersStatuses.filter((status) => status === OfferStatus.Pending).length
      );
    case OfferStatus.Terminating:
      return selectedOffersStatuses.filter((status) => status === OfferStatus.Published).length;
    case OfferStatus.Published:
      return selectedOffersStatuses.filter((status) => status === OfferStatus.Terminating).length;
    default:
      return 0;
  }
}

const handleBulkStateUpdate = async (action) => {
  const offerIds = Array.from(selectedOffers.value.keys());

  const res = await bulkUpdateOffersMutation({
    gameId: gameId.value,
    versionId: versionId.value,
    offerIds,
    status: action,
    useMultiLink: flags.value.offerMultiLinkABTest.enabled,
  });

  const data = res?.data?.bulkUpdateOffers;

  const successfulOfferIds =
    data?.successfulOfferIds?.map((id) => ({
      name: sortedOffers.value.find((offer) => offer.id === id)?.name,
      id,
    })) || [];

  const failedOfferIds =
    data?.failedOfferIds?.map((failedOffer) => ({
      id: failedOffer.id,
      name: sortedOffers.value.find((offer) => offer.id === failedOffer.id)?.name,
    })) || [];

  if (data?.successfulOfferIds.length > 0 && data?.failedOfferIds.length > 0) {
    selectedOffers.value.clear();
    refetchOffers();

    toastWarning({
      detail: `${data.successfulOfferIds.length} ${pluralize(
        "offer",
        data.successfulOfferIds.length
      )} updated successfully: ${successfulOfferIds.map((offer) => offer.name).join(", ")}
      ${data.failedOfferIds.length} ${pluralize("offer", data.failedOfferIds.length)} failed to update: ${failedOfferIds
        .map((offer) => offer.name)
        .join(", ")}`,
      summary: "Warning",
    });

    return;
  }

  if (data?.successfulOfferIds.length > 0) {
    selectedOffers.value.clear();
    refetchOffers();

    toastSuccess({
      detail: `${data.successfulOfferIds.length} ${pluralize(
        "offer",
        data.successfulOfferIds.length
      )} updated successfully`,
      summary: "Success",
      life: 3 * 1000,
    });
  }

  if (data?.failedOfferIds.length > 0) {
    toastError({
      detail: `${data.failedOfferIds.length} ${pluralize(
        "offer",
        data.failedOfferIds.length
      )} failed to update: ${failedOfferIds.map((offer) => offer.name).join(", ")}`,
      summary: "Error",
    });
  }
};

watch([sortedOffers, search], () => {
  handleSearchOffers({ target: { value: search.value } });
});

watch(
  selectedOffers.value,
  () => {
    if (selectedOffers.value.size === 0) {
      selectAll.value = false;
      return;
    }

    selectAll.value = selectedOffers.value.size === visibleOffers.value.length;
  },
  { immediate: true }
);

onMounted(async () => {
  await getCampaigns();
});
</script>

<style module lang="scss">
.headerDescription {
  color: var(--vd-color-grey-light-1);
  margin-top: 0.5rem;
}

.headerSection {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.actionsSection {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;

  div {
    display: flex;
    align-items: center;

    &:last-child {
      gap: 1rem;
    }
  }
}

.expandAllSection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loadingContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
}

.offerItem {
  margin-bottom: 1rem;
  width: 100%;
}

.sortSection {
  display: flex;
  align-items: center;
}

.selectOffersActions {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;
  border: 2px solid var(--vd-color-blue-default);
  border-radius: 4px;
  background: var(--vd-color-blue-light-9);
  color: var(--vd-color-blue-default);
  padding: 0.75rem;
  padding-left: 1rem;
  font-weight: bold;
  // Keep the bulk actions reachable while scrolling a long offers list.
  position: sticky;
  top: 0;
  z-index: 10;
}

.bulkLinkButtonWrapper {
  display: inline-flex;
  vertical-align: top;
  margin-right: 0.75rem;
}

.selectAll {
  display: flex;
  margin-right: 1rem;
  white-space: nowrap;
}

.draftWarning {
  font-weight: normal;
  margin-top: 0.5rem;
  color: var(--vd-color-error);
  font-size: 12px;
}

.popoverOptions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  > div {
    cursor: pointer;
    padding: 0.5rem;

    &:hover {
      background: rgb(243, 244, 246);
    }
  }
}

.campaignUploadOption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
</style>
